import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import gql from 'graphql-tag';
import cloneDeep from 'lodash-es/cloneDeep';
import get from 'lodash-es/get';
import { EMPTY, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { applicationCodes } from 'src/environments/application-codes';
import { IGNORE_ALL_GRAPHQL_ERRORS } from 'src/app/_core/graphql.module';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { LayoutService } from 'src/app/_core/services/layout.service';
import { StoreProviderService } from 'src/app/_core/services/store.provider.service';
import { UserFragment } from 'src/app/_user/fragments/user.fragment';
import { GetMenusListAction } from 'src/app/_user/store/menu/menu.action';
import {
    CheckIfUserAdminInDelegationAction,
    CheckIfUserSuperAdminAction,
    CheckIfUserFirstTimeLoginAction,
    GetCEListAction, GetFunctionalitiesAndWidgetsAccessibleAction,
    GetModulesListAction,
    GetMultiDelegationsListsAction,
    GetPortalAccessBeneficiaryAction,
    LoginAction,
    LogoutAction,
    SaveUserLoginInformationsAction,
    UserUpdateApplicationParametersAction,
    UserUpdatePassword,
} from 'src/app/_user/store/user/user.action';
import { toNumber } from 'src/app/utils/number.utils';
import {
    ArrayOfBaseV2Model,
    ArrayOfModuleInfoDto,
    ArrayOfParametresUserAppliModelDto,
    BaseV2Model,
    BenefBenefPourAccesPortailModel,
    CaisseDto,
    CeDelegationDto,
    ConnexionUtilisateurModelDto,
    ContextInfosInput,
    ListeCodeFonctionnalitesWidgetsAccessibleModel,
    MutationConnexionVupByLoginArgs,
    MutationDeconnecterArgs,
    MutationGetBenefPourAccesPortailArgs,
    MutationGetListeCodeFonctionnaliteWidgetAccessibleArgs,
    MutationGetListeModulesInfoArgs,
    MutationGetListeNomCev2Args,
    MutationGetListesConnexionUtilisateurArgs,
    MutationIsPremiereConnexionVupArgs,
    MutationIsSuperAdministrateurArgs,
    MutationIsUserAdminInDelegationArgs,
    MutationSaveInfosConnexionUserArgs,
    MutationSaveListeApplicationParametersArgs,
    ParametresUserAppliModelDto,
    PathsCeInfos,
    PointVenteDto,
    StockDto,
    Union_29816689623692486759,
    UserCommissionDroit,
    UsersDto
} from 'src/graphql/generated-types';

export interface LoginLists {

    delegationsList: CeDelegationDto[];

    sellsPointList: PointVenteDto[];

    desksList: CaisseDto[];

    stocksList: StockDto[];
}

export interface StoredCredentials {

    login: string;

    ceName: string;

}

export interface StatisticCredentials {
    login: string;

    password: string;

    url: string;
}

export interface NoReplyMailCE {
    useNoReply: boolean;
    noReplyAddress;
}

class UserStateUserable {

    user: any = null;

}

export class UserStateModel extends UserStateUserable {

    userContext: ContextInfosInput = null;

    token: any = null;

    modulesList: string[] = [];

    functionalitiesList: string[] = [];

    delegationsList: CeDelegationDto[] = [];

    sellsPointList: PointVenteDto[] = [];

    desksList: CaisseDto[] = [];

    stocksList: StockDto[] = [];

    ceList: BaseV2Model[] = [];

    isSuperAdmin = false;

    isUserAdminInDelegation = false;

    isFirstTimeLogin = false;

    currentDelegation: CeDelegationDto = null;

    currentSellPoint: PointVenteDto = null;

    currentCaisse: CaisseDto = null;

    credentials: StoredCredentials = null;

    applicationParameters: ParametresUserAppliModelDto[] = [];

    urlPortail: string = null;

    portailProtocol: string = null;

    benefPortailAccess: BenefBenefPourAccesPortailModel = null;

    connectedUser: UsersDto = null;

    commissionsRightsList: UserCommissionDroit[] = [];

    statisticsCredentials: StatisticCredentials = null;

    noReplyMailCE: NoReplyMailCE = null;

    fileDownloadUrl: string = null;

    maxFileSize: number = null;

    pathsCE: PathsCeInfos = null;

    versionVUP: string = null;

    accountingClosingVerificationCode: string = null;

    maxUsersLicencesGeneric = 0;

    maxUsersLicencesNamed = 0;

    constructor(credentials?: StoredCredentials) {
        super();

        this.credentials = credentials || null;
    }
}

@Injectable()
@State<UserStateModel>({
    name: 'user',
    defaults: new UserStateModel()
})
export class UserState {

    constructor(private graphqlService: GraphqlService,
                private store: Store,
                private layoutService: LayoutService) {
    }

// <editor-fold defaultstate="collapsed" desc="Selectors">

    @Selector()
    static pathsCE(state: UserStateModel): PathsCeInfos {
        return state.pathsCE;
    }

    @Selector()
    static userContext(state: UserStateModel): ContextInfosInput {
        return state.userContext;
    }

    @Selector()
    static connectedUser(state: UserStateModel): UsersDto {
        return state.connectedUser;
    }

    @Selector()
    static currentDelegation(state: UserStateModel): CeDelegationDto {
        return state.currentDelegation;
    }

    @Selector()
    static isCurrentDelegationGlobal(state: UserStateModel): boolean {
        return state.currentDelegation && state.currentDelegation.CEIdcGlob;
    }

    @Selector()
    static delegationsList(state: UserStateModel): CeDelegationDto[] {
        return state.delegationsList;
    }

    @Selector()
    static sellsPointList(state: UserStateModel): PointVenteDto[] {
        return state.sellsPointList;
    }

    @Selector()
    static currentSellPoint(state: UserStateModel): PointVenteDto {
        return state.currentSellPoint;
    }

    @Selector()
    static currentCaisse(state: UserStateModel): CaisseDto {
        return state.currentCaisse;
    }

    @Selector()
    static desksList(state: UserStateModel): CaisseDto[] {
        return state.desksList;
    }

    @Selector()
    static stocksList(state: UserStateModel): StockDto[] {
        return state.stocksList;
    }

    @Selector()
    static currentStock(state: UserStateModel): StockDto {
        return state.stocksList.find(({Id}) => Id === state.userContext.CurrentStock);
    }

    @Selector()
    static ceList(state: UserStateModel): BaseV2Model[] {
        return state.ceList;
    }

    @Selector()
    static lists(state: UserStateModel): LoginLists {
        return {
            delegationsList: state.delegationsList,
            sellsPointList: state.sellsPointList,
            desksList: state.desksList,
            stocksList: state.stocksList
        };
    }

    @Selector()
    static needChoice(state: UserStateModel): boolean {
        return state.delegationsList.length > 1 ||
            state.sellsPointList.length > 1 ||
            state.desksList.length > 1 ||
            state.stocksList.length > 1;
    }

    @Selector()
    static commissionsRightsList(state: UserStateModel): UserCommissionDroit[] {
        return state.commissionsRightsList;
    }

    @Selector()
    static isSuperAdmin(state: UserStateModel): boolean {
        return state.isSuperAdmin;
    }

    @Selector()
    static isFirstTimeLogin(state: UserStateModel): boolean {
        return state.isSuperAdmin ? false : state.isFirstTimeLogin;
    }

    @Selector()
    static credentials(state: UserStateModel): StoredCredentials {
        return state.credentials;
    }

    @Selector()
    static applicationParameters(state: UserStateModel): ParametresUserAppliModelDto[] {
        return state.applicationParameters;
    }

    @Selector()
    static isUserAdminInDelegation(state: UserStateModel): boolean {
        return state.isUserAdminInDelegation;
    }

    @Selector()
    static hasActivePortalRight(state: UserStateModel): boolean {
        const rightParameter = state.applicationParameters.find(p => p.CodeChamp === 'GDRPOR');
        return rightParameter && rightParameter.CodeValeur === '1';
    }

    @Selector()
    static canDisplayCommissionsList(state: UserStateModel): boolean {
        return this.hasActivePortalRight(state) && state.isUserAdminInDelegation;
    }

    @Selector()
    static currentCeName(state: UserStateModel): string {
        return state.delegationsList.find(ce => ce.Id === state.userContext.CurrentCE)?.CENom;
    }

    @Selector()
    static urlPortail(state: UserStateModel): string {
        const {urlPortail, portailProtocol} = state;
        return portailProtocol + urlPortail;
    }

    @Selector()
    static benefPortailAccess(state: UserStateModel): BenefBenefPourAccesPortailModel {
        return state.benefPortailAccess;
    }

    @Selector()
    static modulesList(state: UserStateModel): string[] {
        return state.modulesList;
    }

    @Selector()
    static getApplicationParameter(state: UserStateModel): (code: string, activityId?: number) => ParametresUserAppliModelDto {
        return (code: string, activityId?: number) => {
            const getApplicationParameters = (selectedActivityId: number = null) => state.applicationParameters.find(applicationParameter => {
                return applicationParameter.CodeChamp === code && applicationParameter.IdActivite === selectedActivityId;
            });

            // Recupère le parametre d'application correspondant au code et à l'activité si ce paramètre est renseigné
            // Lorsqu'aucun paramètre n'est retourné pour l'activité, on récupère le premier paramètre qui n'est pas lié à une activité (null)
            return (activityId && getApplicationParameters(activityId)) || getApplicationParameters();
        };
    }

    @Selector()
    static hasApplicationParameters(state: UserStateModel): (code: string, activityId?: number) => boolean {
        return (code: string, activityId?: number) => {
            const applicationParameters = this.getApplicationParameter(state)(code, activityId);
            return !applicationParameters || applicationParameters.CodeValeur !== '0';
        };
    }

    @Selector()
    static statisticCredentials(state: UserStateModel): StatisticCredentials {
        return state.statisticsCredentials;
    }

    @Selector()
    static noReplyMailCE(state: UserStateModel): NoReplyMailCE {
        return state.noReplyMailCE;
    }

    @Selector()
    static fileDownloadUrl(state: UserStateModel): string {
        return state.fileDownloadUrl;
    }

    @Selector()
    static maxFileSize(state: UserStateModel): number {
        return state.maxFileSize;
    }

    @Selector()
    static versionVUP(state: UserStateModel): string | null {
        return state.versionVUP;
    }

    @Selector()
    static hasVersion(state: UserStateModel): (version: number) => boolean {
        return (version: number) => {
            const versionVUP = toNumber(state.versionVUP);
            return !!versionVUP && versionVUP >= version;
        };
    }

    @Selector()
    static hasMultiDelegationsModule(state: UserStateModel): boolean {
        return state.modulesList.includes('MULTIDELEG');
    }

    @Selector()
    static hasSeparatedAccountingModule(state: UserStateModel): boolean {
        return state.modulesList.includes('CPTDELEG');
    }

    @Selector()
    static hasMultiStocksModule(state: UserStateModel): boolean {
        return state.modulesList.includes('MULTISTOCK');
    }

    @Selector()
    static hasMultiPurchasePointsModule(state: UserStateModel): boolean {
        return state.modulesList.includes('MULTIPV');
    }

    @Selector()
    static hasAttachmentsModule(state: UserStateModel): boolean {
        return state.modulesList.includes('PJCOMPTA');
    }

    @Selector()
    static hasIToolModule(state: UserStateModel): boolean {
        return state.modulesList.includes('EXP_ITOOLS_AUTO');
    }

    @Selector()
    static hasPrintOptionModule(state: UserStateModel): boolean {
        return state.modulesList.includes(applicationCodes.ENGAGEMENT_BUDGETAIRE);
    }

    @Selector()
    static hasModules(codes: string | string[]): (state) => boolean {
        return ({user}): boolean => (Array.isArray(codes) ? codes : [codes])
            .every(moduleCode => (user.modulesList || []).includes(moduleCode));
    }

    @Selector()
    static hasFunctionality(code: string): (state) => boolean {
        return ({user}): boolean => (user.functionalitiesList || []).includes(code);
    }

    @Selector()
    static accountingClosingVerificationCode(state: UserStateModel): string | null {
        return state.accountingClosingVerificationCode;
    }

    @Selector()
    static maxUsersLicencesGeneric(state: UserStateModel): number {
        return state.maxUsersLicencesGeneric;
    }

    @Selector()
    static maxUsersLicencesNamed(state: UserStateModel): number {
        return state.maxUsersLicencesNamed;
    }

// </editor-fold>

// <editor-fold defaultstate="collapsed" desc="Actions">

// <editor-fold defaultstate="collapsed" desc="Login / Logout">

    @Action(LoginAction)
    login(
        state: StateContext<UserStateModel>,
        {credentials: {login, password, ceName, storeCredentials}}: LoginAction
    ): Observable<void> {
        return this.graphqlService.mutateOne<'ConnexionVupByLogin', MutationConnexionVupByLoginArgs>({
            mutation: gql`
                mutation connection($userLogin: String, $userPassword: String, $nomCe: String) {
                    ConnexionVupByLogin(userLogin: $userLogin, userPassword: $userPassword, nomCe: $nomCe) {
                        Contexte {
                            CurrentCE
                            CurrentPV
                            CurrentCaisse
                            CurrentStock
                            CurrentUserId
                            NumSession
                            IsSuperAdministrateur
                        }
                        ListeCodesFonctionnalitesAccessible {
                            string
                        }
                        CurrentCaisse {
                            IsCentrale
                            Id
                            Code
                            IsRemiseCaisseCentrale
                        }
                        CurrentDelegation {
                            ...ceDelegation
                        }
                        CurrentUser {
                            Id
                            Nom
                            Prenom
                            Login
                            Password
                            PasswordInitial
                            IsGenerique
                            MailPerso
                            MailPro
                            ListeUsersCE {
                                UsersCEDelegationDTO {
                                    CEDelegation {
                                        ...ceDelegation
                                    }
                                }
                            }
                            ListeUsersPV {
                                UsersPointVenteDTO {
                                    PointVente {
                                        ID
                                        PVCodl
                                        PVLib
                                    }
                                }
                            }
                            ListeCaisse {
                                CaisseDTO {
                                    Id
                                    Code
                                    Libelle
                                    IsRemiseCaisseCentrale
                                }
                            }
                            ListeStock {
                                StockDTO {
                                    Id
                                    Code
                                    Libelle
                                }
                            }
                        }
                        CurrentDelegation {
                            Id
                            CEIdcGlob
                        }
                        CurrentPointVente {
                            ID
                            PVCodl
                            PVLib
                            CEDelegation {
                                Id
                            }
                        }
                        ListeUserCommissionDroit {
                            UserCommissionDroit {
                                IdCommission
                                CodeDroitSurCommission
                            }
                        }
                        UrlPortail
                        PathsCE {
                            PortailProtocol
                            DailymotionUrl
                            YoutubeUrl
                            GestionUrlBase
                            NomCE
                        }
                        ParametresCE {
                            CodeValidationClotureComptable
                            PiwikLogin
                            PiwikPassword
                            PiwikUrlSite
                            UtiliserEmailNoReplyCommeExpediteur
                            EmailNoReplySender
                            PoidsFichierMax
                            NombreLicencesUtilisateurGenerique
                            NombreLicencesUtilisateurNomme
                        }
                        VersionVUP
                    }
                }
                ${UserFragment.ceDelegation}
            `,
            variables: {
                userLogin: login,
                userPassword: password,
                nomCe: ceName
            },
            context: {[IGNORE_ALL_GRAPHQL_ERRORS]: true}
        })
            .pipe(
                tap((ConnexionVupByLogin) => {
                    const {PathsCE, ParametresCE} = ConnexionVupByLogin;
                    state.patchState({
                        isSuperAdmin: ConnexionVupByLogin.Contexte.IsSuperAdministrateur,
                        pathsCE: PathsCE,
                        functionalitiesList: get(ConnexionVupByLogin.ListeCodesFonctionnalitesAccessible, 'string', []),
                        userContext: ConnexionVupByLogin.Contexte,
                        currentDelegation: ConnexionVupByLogin.CurrentDelegation,
                        currentSellPoint: ConnexionVupByLogin.CurrentPointVente,
                        currentCaisse: ConnexionVupByLogin.CurrentCaisse,
                        credentials: storeCredentials ? {login, ceName} : null,
                        urlPortail: ConnexionVupByLogin.UrlPortail,
                        portailProtocol: PathsCE.PortailProtocol,
                        connectedUser: ConnexionVupByLogin.CurrentUser,
                        commissionsRightsList: get(ConnexionVupByLogin.ListeUserCommissionDroit, 'UserCommissionDroit', []),
                        statisticsCredentials: {
                            login: ParametresCE.PiwikLogin,
                            password: ParametresCE.PiwikPassword,
                            url: ParametresCE.PiwikUrlSite
                        },
                        noReplyMailCE: {
                            useNoReply: ParametresCE.UtiliserEmailNoReplyCommeExpediteur === '1',
                            noReplyAddress: ParametresCE.EmailNoReplySender
                        },
                        fileDownloadUrl: (PathsCE.GestionUrlBase.slice(-1) === '/' ? PathsCE.GestionUrlBase : PathsCE.GestionUrlBase + '/') + 'Download/ExportFile',
                        maxFileSize: ParametresCE?.PoidsFichierMax,
                        versionVUP: ConnexionVupByLogin.VersionVUP || null,
                        accountingClosingVerificationCode: ParametresCE?.CodeValidationClotureComptable,
                        maxUsersLicencesGeneric: ParametresCE.NombreLicencesUtilisateurGenerique,
                        maxUsersLicencesNamed: ParametresCE.NombreLicencesUtilisateurNomme,
                    });
                }),
                switchMap((ConnexionVupByLogin) => state.dispatch(new CheckIfUserFirstTimeLoginAction()).pipe(
                    switchMap(() => state.getState().isFirstTimeLogin && !state.getState().isSuperAdmin ? EMPTY : of(ConnexionVupByLogin))
                )),
                switchMap((ConnexionVupByLogin) => this.manageMultiDelegations(ConnexionVupByLogin.CurrentUser, state))
            );
    }

    @Action(LogoutAction)
    logout({getState}: StateContext<UserStateModel>): Observable<string> {
        const previousState = getState();

        return this.graphqlService.mutateOne<'Deconnecter', MutationDeconnecterArgs>({
            mutation: gql`
                mutation logout($contexte: ContextInfosInput) {
                    Deconnecter(contexte: $contexte)
                }
            `,
        })
            .pipe(
                tap(() => {
                    // il faut remettre à 0 tous les states déjà préchargés
                    this.store.reset({
                        ...StoreProviderService.initialStates,
                        user: new UserStateModel(previousState.credentials)
                    });
                })
            );
    }

    @Action(CheckIfUserSuperAdminAction)
    checkIfUserSuperAdmin(
        {patchState, dispatch}: StateContext<UserStateModel>,
        {credentials: {login, password}}: CheckIfUserSuperAdminAction
    ): Observable<void> {
        return this.graphqlService.mutateOne<'IsSuperAdministrateur', MutationIsSuperAdministrateurArgs>({
            mutation: gql`
                mutation isSuperAdmin($userLogin: String, $userPassword: String) {
                    IsSuperAdministrateur(userLogin: $userLogin, userPassword: $userPassword)
                }
            `,
            variables: {userLogin: login, userPassword: password}
        }).pipe(
            switchMap((IsSuperAdministrateur) => {
                patchState({isSuperAdmin: !!IsSuperAdministrateur});
                return !!IsSuperAdministrateur ? dispatch(new GetCEListAction({login, password})) : EMPTY;
            })
        );
    }

    @Action(CheckIfUserAdminInDelegationAction)
    checkIfUserAdminInDelegation({getState, patchState}: StateContext<UserStateModel>): Observable<boolean> {
        const {CurrentUserId, CurrentCE} = getState().userContext;

        return this.graphqlService.mutateOne<'IsUserAdminInDelegation', MutationIsUserAdminInDelegationArgs>({
            mutation: gql`
                mutation isUserAdminInDeleg($contexte: ContextInfosInput, $idCe: Float, $idUser: Float) {
                    IsUserAdminInDelegation(contexte: $contexte, idCe: $idCe, idUser: $idUser)
                }
            `,
            variables: {idCe: CurrentCE, idUser: CurrentUserId}
        })
            .pipe(
                tap((IsUserAdminInDelegation) => patchState({isUserAdminInDelegation: IsUserAdminInDelegation})
                ),
                catchError(err => {
                    return throwError(err);
                }),
            );
    }

    @Action(CheckIfUserFirstTimeLoginAction)
    checkIfUserFirstTimeLogin({patchState}: StateContext<UserStateModel>): Observable<boolean> {
        return this.graphqlService.mutateOne<'IsPremiereConnexionVUP', MutationIsPremiereConnexionVupArgs>({
            mutation: gql`
                mutation IsPremiereConnexionVUP($contexte: ContextInfosInput) {
                    IsPremiereConnexionVUP(contexte: $contexte)
                }
            `
        }).pipe(
            tap(IsPremiereConnexionVUP => patchState({isFirstTimeLogin: !!IsPremiereConnexionVUP}))
        );
    }

    @Action(SaveUserLoginInformationsAction)
    saveUserLoginInformations(
        {getState, patchState, dispatch}: StateContext<UserStateModel>,
        {loginInformations: {delegationId, sellPointId, deskId, stockId}}: SaveUserLoginInformationsAction
    ): Observable<void> {
        return this.graphqlService.mutateOne<'SaveInfosConnexionUser', MutationSaveInfosConnexionUserArgs>({
            mutation: gql`
                mutation saveInformations($caisseId: Float, $contexte: ContextInfosInput, $delegId: Float, $pvId: Float, $stockId: Float, $userId: Float) {
                    SaveInfosConnexionUser(contexte: $contexte, caisseId: $caisseId, delegId: $delegId, pvId: $pvId, stockId: $stockId, userId: $userId) {
                        ParametresUserAppliModelDTO {
                            ...applicationParameter
                        }
                    }
                }
                ${UserFragment.applicationParameter}
            `,
            variables: {
                caisseId: deskId,
                delegId: delegationId,
                pvId: sellPointId,
                stockId,
                userId: getState().userContext.CurrentUserId
            }
        })
            .pipe(
                tap((SaveInfosConnexionUser) => {
                    const applicationParameters = get(SaveInfosConnexionUser, 'ParametresUserAppliModelDTO', []);

                    const userContext = cloneDeep(getState().userContext);

                    const currentDelegation = getState().delegationsList.find(d => d.Id === delegationId);

                    const currentSellPoint = getState().sellsPointList.find(d => d.ID === sellPointId);

                    const currentCaisse = getState().desksList.find(d => d.Id === deskId);

                    userContext.CurrentCE = delegationId;
                    userContext.CurrentPV = sellPointId;
                    userContext.CurrentCaisse = deskId;
                    userContext.CurrentStock = stockId;

                    patchState({userContext, currentDelegation, currentSellPoint, applicationParameters, currentCaisse});
                }),
                tap(() => this.layoutService.clearMenuAccessCache()),
                switchMap(() => dispatch(new CheckIfUserAdminInDelegationAction())),
                switchMap(() => dispatch(new GetModulesListAction())),
                switchMap(() => dispatch(new GetMenusListAction())),
            );
    }

// </editor-fold>

// <editor-fold defaultstate="collapsed" desc="Multi delegations">

    @Action(GetMultiDelegationsListsAction)
    getMultiDelegationsLists(
        {patchState, getState, dispatch}: StateContext<UserStateModel>): Observable<ConnexionUtilisateurModelDto> {

        return this.graphqlService.mutateOne<'GetListesConnexionUtilisateur', MutationGetListesConnexionUtilisateurArgs>({
            mutation: gql`
                mutation getMultiDelegationsLists($contexte: ContextInfosInput, $UserId: Float) {
                    GetListesConnexionUtilisateur(contexte: $contexte, UserId: $UserId) {
                        ListeCEDelegation {
                            CEDelegationDTO {
                                ...ceDelegation
                            }
                        }
                        ListePointVente {
                            PointVenteDTO {
                                ID
                                PVCodl
                                PVLib
                                CEDelegation {
                                    Id
                                }
                            }
                        }
                        ListeCaisse {
                            CaisseDTO {
                                Id
                                Code
                                Libelle
                                IsCentrale
                                IsRemiseCaisseCentrale
                                PV {
                                    ID
                                }
                            }
                        }
                        ListeStock {
                            StockDTO {
                                Id
                                Code
                                Libelle
                                PV {
                                    ID
                                }
                            }
                        }
                    }
                }
                ${UserFragment.ceDelegation}
            `,
            variables: {UserId: getState().userContext.CurrentUserId}
        })
            .pipe(
                tap((GetListesConnexionUtilisateur) => {
                    patchState({
                        delegationsList: GetListesConnexionUtilisateur?.ListeCEDelegation?.CEDelegationDTO || [],
                        sellsPointList: GetListesConnexionUtilisateur?.ListePointVente?.PointVenteDTO || [],
                        desksList: GetListesConnexionUtilisateur?.ListeCaisse?.CaisseDTO || [],
                        stocksList: GetListesConnexionUtilisateur?.ListeStock?.StockDTO || [],
                    });

                    // Si 1 seul choix possible par liste, on mets les ids dans le state
                    if(!UserState.needChoice(getState())) {
                        dispatch(new SaveUserLoginInformationsAction({
                            delegationId: getState().delegationsList[0].Id,
                            sellPointId: getState().sellsPointList[0].ID,
                            deskId: getState().desksList[0].Id,
                            stockId: getState().stocksList[0].Id,
                        }));
                    }
                }),
            );
    }

// </editor-fold>

    @Action(GetCEListAction)
    getCEList(
        {patchState, getState}: StateContext<UserStateModel>,
        {credentials: {login, password}}: GetCEListAction
    ): Observable<ArrayOfBaseV2Model> {
        return this.graphqlService.mutateOne<'GetListeNomCEV2', MutationGetListeNomCev2Args>({
            mutation: gql`
                mutation getCEList($userLogin: String, $userPassword: String) {
                    GetListeNomCEV2(userLogin: $userLogin, userPassword: $userPassword) {
                        BaseV2Model {
                            InitialCatalog
                            NomBase
                        }
                    }
                }
            `,
            variables: {userLogin: login, userPassword: password}
        })
            .pipe(
                tap((GetListeNomCEV2) => {
                    GetListeNomCEV2.BaseV2Model.sort((a, b) => {
                        if (a.NomBase.toLocaleLowerCase() < b.NomBase.toLocaleLowerCase()) {
                            return -1;
                        }
                        if (a.NomBase.toLocaleLowerCase() > b.NomBase.toLocaleLowerCase()) {
                            return 1;
                        }
                        return 0;
                    });

                    patchState({ceList: GetListeNomCEV2.BaseV2Model});
                }),
            );
    }

    @Action(GetModulesListAction)
    getModulesList({patchState}: StateContext<UserStateModel>): Observable<ArrayOfModuleInfoDto> {
        return this.graphqlService.mutateOne<'GetListeModulesInfo', MutationGetListeModulesInfoArgs>({
            mutation: gql`
                mutation getModulesList($contexte: ContextInfosInput) {
                    GetListeModulesInfo(contexte: $contexte) {
                        ModuleInfoDTO {
                            Code
                        }
                    }
                }
            `,
        })
            .pipe(
                tap((GetListeModulesInfo) => {
                    patchState({
                        modulesList: GetListeModulesInfo.ModuleInfoDTO.map(e => e.Code)
                    });
                }),
            );
    }

    @Action(GetFunctionalitiesAndWidgetsAccessibleAction)
    GetFunctionalitiesAndWidgetsAccessible({patchState, getState}: StateContext<UserStateModel>): Observable<ListeCodeFonctionnalitesWidgetsAccessibleModel> {
        const {CurrentUserId, CurrentCE} = getState().userContext;

        return this.graphqlService.mutateOne<'GetListeCodeFonctionnaliteWidgetAccessible', MutationGetListeCodeFonctionnaliteWidgetAccessibleArgs>({
            mutation: gql`
                mutation GetListeCodeFonctionnaliteWidgetAccessible(
                    $contexte: ContextInfosInput,
                    $idUser: Float,
                    $idCe: Float
                ) {
                    GetListeCodeFonctionnaliteWidgetAccessible(
                        contexte: $contexte,
                        idUser: $idUser,
                        idCe: $idCe
                    ) {
                        ListeCodesFonctionnalitesDisponibles {
                            string
                        }
                        ListeCodesWidgetsdisponibles {
                            string
                        }
                    }
                }
            `,
            variables: {
                idUser: CurrentUserId,
                idCe: CurrentCE
            }
        }).pipe(
                tap((GetFunctionalitiesAndWidgetsAccessibleAction) => {
                    patchState({
                        functionalitiesList: GetFunctionalitiesAndWidgetsAccessibleAction?.ListeCodesFonctionnalitesDisponibles?.string || []
                    });
                }),
            );
    }

    @Action(GetPortalAccessBeneficiaryAction)
    getPortalAccessBeneficiary({
                                   patchState,
                                   getState
                               }: StateContext<UserStateModel>): Observable<Union_29816689623692486759> {
        return this.graphqlService.mutateOne<'GetBenefPourAccesPortail', MutationGetBenefPourAccesPortailArgs>({
            mutation: gql`
                mutation getBenefPourAccesPortail($contexte: ContextInfosInput $criteres: CriteresBenefBaseModelInput) {
                    GetBenefPourAccesPortail(contexte: $contexte criteres: $criteres) {
                        ... on BenefBenefPourAccesPortailModel {
                            Id
                            Nom
                            Prenom
                            PeutAccederAuPortail
                        }
                    }
                }
            `,
            variables: {
                criteres: {
                    UserId: getState().connectedUser.Id,
                }
            }
        })
            .pipe(
                tap((GetBenefPourAccesPortail) => {
                    patchState({
                        benefPortailAccess: GetBenefPourAccesPortail as BenefBenefPourAccesPortailModel
                    });
                })
            );
    }

    @Action(UserUpdateApplicationParametersAction)
    updateApplicationParameters({patchState, getState}: StateContext<UserStateModel>,
                                {parameters}: UserUpdateApplicationParametersAction): Observable<ArrayOfParametresUserAppliModelDto> {
        return this.graphqlService.mutateOne<'SaveListeApplicationParameters', MutationSaveListeApplicationParametersArgs>
        ({
            mutation: gql`
                mutation SaveListeApplicationParameters($contexte: ContextInfosInput, $ListeParametreValeur: ArrayOfParametresUserAppliModelDTOInput){
                    SaveListeApplicationParameters(contexte: $contexte, ListeParametreValeur: $ListeParametreValeur){
                        ParametresUserAppliModelDTO {
                            ...applicationParameter
                        }
                    }
                }
                ${UserFragment.applicationParameter}
            `,
            variables: {
                ListeParametreValeur: {ParametresUserAppliModelDTO: parameters.filter(param => !!param)},
            }
        })
            .pipe(
                tap((SaveListeApplicationParameters) => {
                    const applicationParameters = cloneDeep(getState().applicationParameters);
                    const updatedParameters: ParametresUserAppliModelDto[] = SaveListeApplicationParameters?.ParametresUserAppliModelDTO || [];

                    updatedParameters.forEach(updatedParameter => {
                        const initialParameter = applicationParameters.find((initialParam: ParametresUserAppliModelDto) => {
                            return initialParam.CodeChamp === updatedParameter.CodeChamp &&
                                (updatedParameter.IdActivite ? initialParam.IdActivite === updatedParameter.IdActivite : true);
                        });

                        if (!initialParameter) {
                            applicationParameters.push(updatedParameter);
                        } else {
                            Object.assign(initialParameter, updatedParameter);
                        }
                    });

                    patchState({applicationParameters});
                }),
                catchError(err => {
                    return throwError(err);
                })
            );
    }

    @Action(UserUpdatePassword)
    updatePassword({patchState, getState}: StateContext<UserStateModel>, {newEncryptedPassword}: UserUpdatePassword): void {
        patchState({
            connectedUser: {
                ...getState().connectedUser,
                Password: newEncryptedPassword
            }
        });
    }


// </editor-fold>

// <editor-fold defaultstate="collapsed" desc="Private functions">

// <editor-fold defaultstate="collapsed" desc="Multi delegations">

    private manageMultiDelegations(user: UsersDto, {
        patchState,
        dispatch
    }: StateContext<UserStateModel>): Observable<void> {
        if (this.needChoice(user)) {
            return dispatch(new GetMultiDelegationsListsAction());
        } else {
            const delegationsList = user.ListeUsersCE.UsersCEDelegationDTO.map(u => u.CEDelegation);
            const sellsPointList = user.ListeUsersPV.UsersPointVenteDTO.map(u => u.PointVente);
            const desksList = user.ListeCaisse.CaisseDTO;
            const stocksList = user.ListeStock.StockDTO;

            patchState({
                delegationsList,
                sellsPointList,
                desksList,
                stocksList
            });

            return dispatch(new SaveUserLoginInformationsAction({
                delegationId: delegationsList[0].Id,
                sellPointId: sellsPointList[0].ID,
                deskId: desksList[0].Id,
                stockId: stocksList[0].Id,
            }));
        }
    }

    private needChoice(user: UsersDto): boolean {
        return user.ListeUsersCE.UsersCEDelegationDTO.length > 1 ||
            user.ListeUsersPV.UsersPointVenteDTO.length > 1 ||
            user.ListeCaisse.CaisseDTO.length > 1 ||
            user.ListeStock.StockDTO.length > 1;
    }

// </editor-fold>

// </editor-fold>

}
